import React, { useState } from 'react';
import { Flex, Div, Gutter, Title, BodyText, GridBag } from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';
import { LocaleResponse } from '@cdaas/api-clients/dist/product-service-openapi';

import EditableItem from '../../../../shared/EditableItem/EditableItem';
import { removeEntityByIndex, updateEntityByIndex } from '../../../../utils/entities';
import { Errors } from '../utils';

interface BoxContentsProps {
  title: string;
  boxContents: string[] | undefined;
  boxContentsTranslations: { localeId: number; translations: string[] }[] | undefined;
  locale: LocaleResponse;
  handleBoxContentsChange: (boxContents: string[]) => void;
  handleBoxContentsTranslationsChange: (translations: { localeId: number; translations: string[] }[]) => void;
  errors: Errors['boxContents'];
}

function BoxContents(props: BoxContentsProps) {
  const { title, boxContents, boxContentsTranslations, locale, handleBoxContentsChange, handleBoxContentsTranslationsChange, errors } = props;
  const { t } = useTranslation('productscreen');

  const [referenceInputsOpen, setReferenceInputsOpen] = useState<boolean[]>(new Array(boxContents?.length).fill(false));

  const setReferenceInputOpenState = (index: number, open: boolean) => {
    const openReferenceInputsState = [...referenceInputsOpen];
    openReferenceInputsState[index] = open;
    setReferenceInputsOpen(openReferenceInputsState);
  };

  const handleBoxContentAdd = () => {
    const updatedBoxContents = [...(boxContents || []), ''];
    const updatedTranslations = boxContentsTranslations?.map(translation => {
      return {
        ...translation,
        translations: [...(translation.translations || []), ''],
      };
    });
    handleBoxContentsChange(updatedBoxContents);
    if (updatedTranslations) handleBoxContentsTranslationsChange(updatedTranslations);
    setReferenceInputsOpen([...referenceInputsOpen, false]);
  };

  const handleBoxContentRemoval = (index: number) => {
    const updatedBoxContents = removeEntityByIndex(boxContents, index);
    const updatedTranslations = boxContentsTranslations?.map(translation => {
      return {
        ...translation,
        translations: removeEntityByIndex(translation.translations, index),
      };
    });
    handleBoxContentsChange(updatedBoxContents);
    if (updatedTranslations) handleBoxContentsTranslationsChange(updatedTranslations);
    const openReferenceInputsState = removeEntityByIndex(referenceInputsOpen, index);
    setReferenceInputsOpen(openReferenceInputsState);
  };

  const handleBoxContentUpdate = (index: number, updatedBoxContent: string) => {
    const updatedBoxContents = updateEntityByIndex(boxContents, index, updatedBoxContent);
    handleBoxContentsChange(updatedBoxContents);
  };

  const handleTranslationBoxContentChange = (index: number, updatedBoxContentTranslation: string) => {
    const translationIndex = (boxContentsTranslations || []).findIndex(translation => translation.localeId === locale.id);
    if (translationIndex !== -1 && boxContentsTranslations) {
      const translation = boxContentsTranslations[translationIndex];
      const updatedBoxContentTranslations = updateEntityByIndex(translation.translations, index, updatedBoxContentTranslation);
      const updatedTranslation = { ...translation, translations: updatedBoxContentTranslations };
      const updatedTranslations = updateEntityByIndex(boxContentsTranslations, translationIndex, updatedTranslation);
      handleBoxContentsTranslationsChange(updatedTranslations);
    }
  };

  const currentBoxContentsTranslation = boxContentsTranslations?.find(translation => translation.localeId === locale.id)?.translations;

  return (
    <Flex directionColumn>
      <Title size="md">{title}</Title>
      <Gutter />
      <BodyText color="gray400">{t('box_contents_section.info')}</BodyText>
      <Gutter />
      {errors?.boxContentsGeneral && (
        <BodyText size="sm" color="red600">
          {errors.boxContentsGeneral}
        </BodyText>
      )}
      {(currentBoxContentsTranslation || []).map((boxContent, index) => (
        <Div key={index}>
          <GridBag>
            <GridBag.Item md={7}>
              <EditableItem
                backgroundHoverColor="gray200"
                backgroundColor="gray100"
                divProps={{
                  padding: { size: 'sm' },
                }}
              >
                <EditableItem.Input
                  value={boxContent}
                  edit={true}
                  inputProps={{
                    name: `boxContentTranslation-${index}`,
                    onBlur: (e: React.FocusEvent<HTMLInputElement>) => handleTranslationBoxContentChange(index, e.target.value),
                    placeholder: t('box_contents_section.item_translation_placeholder', { language: locale.name.split(' ')[0] }),
                    label: t('box_contents_section.item_translation_label', { index: index + 1 }),
                    onKeyDown: () => undefined,
                  }}
                />
                <Gutter size="sm" />
                <EditableItem.Input
                  value={boxContents?.[index]}
                  edit={referenceInputsOpen[index]}
                  isReference={true}
                  focusOnEdit={true}
                  showDelete
                  inputProps={{
                    name: `boxContent-${index}`,
                    onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                      handleBoxContentUpdate(index, e.target.value);
                      setReferenceInputOpenState(index, false);
                    },
                    placeholder: t('box_contents_section.item_reference_placeholder'),
                    label: t('box_contents_section.item_reference_label', { index: index + 1 }),
                    onKeyDown: () => undefined,
                    required: true,
                    errorText: errors?.boxContents?.find(boxContentError => boxContentError.index === index)?.error,
                  }}
                  editIconProps={{
                    'data-testid': `boxContent-${index}-edit`,
                    onClick: () => setReferenceInputOpenState(index, true),
                  }}
                  deleteIconProps={{
                    'data-testid': `boxContent-${index}-delete`,
                    onClick: () => handleBoxContentRemoval(index),
                  }}
                />
              </EditableItem>
            </GridBag.Item>
          </GridBag>
          <Gutter size="sm" />
        </Div>
      ))}
      <Div>
        <BodyText clickableAppearance={true} color="purple500" onClick={() => handleBoxContentAdd()}>
          {`+ ${t('box_contents_section.add_box_content')}`}
        </BodyText>
      </Div>
    </Flex>
  );
}

export default React.memo(BoxContents);
